import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageSquare,
  Building2,
  MapPin,
  Users,
  Calendar,
  Edit,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Plus,
  Send,
  Loader2,
  Shield,
  RefreshCw,
  Trash2,
  UserCheck,
  Star,
  Timer,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { leadsAPI, usersAPI, demosAPI, dealsAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import EmailComposer from '../../components/EmailComposer';
import WhatsAppChatHistory from '../../components/leads/WhatsAppChatHistory';

const leadStatuses = [
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'CALLBACK_SCHEDULED', label: 'Callback Scheduled' },
  { value: 'REATTEMPT_REQUIRED', label: 'Reattempt Required' },
  { value: 'ATTEMPTED_NO_ANSWER', label: 'Attempted - No Answer' },
  { value: 'NUMBER_SWITCHED_OFF', label: 'Number Switched Off' },
  { value: 'WRONG_NUMBER', label: 'Wrong Number' },
  { value: 'NOT_INTERESTED', label: 'Not Interested' },
  { value: 'NOT_RELEVANT_NOW', label: 'Not Relevant Now' },
  { value: 'CALL_LATER_TODAY', label: 'Call Later Today' },
  { value: 'CALL_LATER_THIS_WEEK', label: 'Call Later This Week' },
  { value: 'CALL_LATER_NEXT_MONTH', label: 'Call Later Next Month' },
  { value: 'INTERESTED_NEEDS_DETAILS_FIRST', label: 'Interested - Needs Details' },
  { value: 'DETAILS_SENT_WAITING_RESPONSE', label: 'Details Sent - Waiting Response' },
  { value: 'INTERESTED_NEEDS_INTERNAL_DISCUSSION', label: 'Needs Internal Discussion' },
  { value: 'FOLLOW_UP_AFTER_DISCUSSION', label: 'Follow Up After Discussion' },
  { value: 'SCHEDULE_DEMO_REQUESTED', label: 'Demo Requested' },
  { value: 'DEMO_SCHEDULED', label: 'Demo Scheduled' },
  { value: 'DEMO_DONE_WAITING_QUALIFICATION', label: 'Demo Done - Awaiting Qualification' },
  { value: 'DEMO_QUALIFIED_SQD', label: 'Demo Qualified (SQD)' },
  { value: 'DEMO_NOT_QUALIFIED', label: 'Demo Not Qualified' },
  { value: 'DO_NOT_DISTURB_REQUESTED', label: 'Do Not Disturb' },
  { value: 'REJECTED_BY_EMPLOYEE', label: 'Rejected' }
];

const industries = [
  'IT Services', 'Retail', 'Healthcare', 'Manufacturing', 'Banking & Finance',
  'Education', 'Logistics', 'Pharmaceutical', 'Real Estate', 'Hospitality', 'Others'
];

const hrmsTools = [
  'None', 'Excel/Manual', 'SAP SuccessFactors', 'Workday', 'Oracle HCM', 'BambooHR', 
  'Zoho People', 'Keka', 'Darwinbox', 'GreytHR', 'sumHR', 'Tally', 'Other'
];

const getStatusColor = (status) => {
  const colors = {
    NEW: 'bg-blue-100 text-blue-700',
    DEMO_SCHEDULED: 'bg-purple-100 text-purple-700',
    DEMO_QUALIFIED_SQD: 'bg-emerald-100 text-emerald-700',
    DEMO_DONE_WAITING_QUALIFICATION: 'bg-amber-100 text-amber-700',
    CALL_LATER_TODAY: 'bg-amber-100 text-amber-700',
    CALL_LATER_THIS_WEEK: 'bg-orange-100 text-orange-700',
    INTERESTED_NEEDS_DETAILS_FIRST: 'bg-cyan-100 text-cyan-700',
    NOT_INTERESTED: 'bg-red-100 text-red-700',
    REJECTED_BY_EMPLOYEE: 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

const getActivityIcon = (type) => {
  switch (type) {
    case 'CALL_ATTEMPT': return <Phone className="w-4 h-4 text-blue-600" />;
    case 'STATUS_CHANGE': return <RefreshCw className="w-4 h-4 text-purple-600" />;
    case 'WHATSAPP_MESSAGE': return <MessageSquare className="w-4 h-4 text-green-600" />;
    case 'EMAIL_SENT': return <Mail className="w-4 h-4 text-orange-600" />;
    case 'DEMO_SCHEDULED': return <Calendar className="w-4 h-4 text-indigo-600" />;
    case 'DEMO_COMPLETED': return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    case 'DEAL_CREATED': return <FileText className="w-4 h-4 text-pink-600" />;
    case 'NOTE_ADDED': return <FileText className="w-4 h-4 text-slate-600" />;
    case 'REASSIGNMENT': return <UserCheck className="w-4 h-4 text-cyan-600" />;
    default: return <Clock className="w-4 h-4 text-slate-600" />;
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case 'CALL_ATTEMPT': return 'bg-blue-100';
    case 'STATUS_CHANGE': return 'bg-purple-100';
    case 'WHATSAPP_MESSAGE': return 'bg-green-100';
    case 'EMAIL_SENT': return 'bg-orange-100';
    case 'DEMO_SCHEDULED': return 'bg-indigo-100';
    case 'DEMO_COMPLETED': return 'bg-emerald-100';
    case 'DEAL_CREATED': return 'bg-pink-100';
    case 'REASSIGNMENT': return 'bg-cyan-100';
    default: return 'bg-slate-100';
  }
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { confirm, dialogProps } = useConfirm();
  
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [demos, setDemos] = useState([]);
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [countdown, setCountdown] = useState(6);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLead, setEditedLead] = useState(null);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Modal states
  const [isScheduleDemoOpen, setIsScheduleDemoOpen] = useState(false);
  const [isStatusChangeOpen, setIsStatusChangeOpen] = useState(false);
  const [isRescheduleDemoOpen, setIsRescheduleDemoOpen] = useState(false);
  const [isCompleteDemoOpen, setIsCompleteDemoOpen] = useState(false);
  const [isDeleteLeadOpen, setIsDeleteLeadOpen] = useState(false);
  const [isReassignOpen, setIsReassignOpen] = useState(false);

  // Form states
  const [demoForm, setDemoForm] = useState({
    demo_date: '',
    demo_time: '',
    duration: '60',
    notes: '',
    meeting_link: ''
  });
  
  const [statusChangeForm, setStatusChangeForm] = useState({
    new_status: '',
    notes: '',
    next_follow_up_date: '',
    next_follow_up_time: ''
  });
  
  const [rescheduleForm, setRescheduleForm] = useState({
    demo_date: '',
    demo_time: '',
    reschedule_reason: '',
    remarks: ''
  });
  
  const [completeDemoForm, setCompleteDemoForm] = useState({
    demo_done_by: [''],
    demo_rating: 5,
    demo_actual_start: '',
    demo_actual_end: '',
    demo_notes: ''
  });
  
  const [reassignForm, setReassignForm] = useState({
    new_assignee_id: '',
    notes: ''
  });
  
  const [selectedDemo, setSelectedDemo] = useState(null);
  
  // Missing fields for demo scheduling
  const [missingFieldsOpen, setMissingFieldsOpen] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // Check if lead can be edited (no completed demo)
  const hasCompletedDemo = demos.some(d => 
    d.status === 'COMPLETED_QUALIFIED_SQD' || d.status === 'COMPLETED_NOT_QUALIFIED'
  );
  
  // Check if demo exists
  const hasScheduledDemo = demos.some(d => d.status === 'SCHEDULED' || d.status === 'RESCHEDULED');

  // Handle Schedule Demo click with validation
  const handleScheduleDemoClick = () => {
    const missing = [];
    if (!lead.contact_email) missing.push('Email');
    if (!lead.city) missing.push('City');
    if (!lead.total_employee_count) missing.push('Employee Count');
    if (!lead.industry) missing.push('Industry');
    if (lead.using_hrms_currently === null || lead.using_hrms_currently === undefined) missing.push('Using HRMS Currently');
    if (lead.using_hrms_currently && !lead.current_hrms_tool) missing.push('Current HRMS Tool');
    
    if (missing.length > 0) {
      setMissingFields(missing);
      setMissingFieldsOpen(true);
    } else {
      setIsScheduleDemoOpen(true);
    }
  };

  // Calculate demo duration
  const calculateDemoDuration = () => {
    if (completeDemoForm.demo_actual_start && completeDemoForm.demo_actual_end) {
      const start = new Date(completeDemoForm.demo_actual_start);
      const end = new Date(completeDemoForm.demo_actual_end);
      const diffMs = end - start;
      const diffMins = Math.round(diffMs / 60000);
      return diffMins > 0 ? diffMins : 0;
    }
    return 0;
  };

  // Redirect countdown for unauthorized access
  useEffect(() => {
    if (unauthorized && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (unauthorized && countdown === 0) {
      navigate('/leads');
    }
  }, [unauthorized, countdown, navigate]);

  // Fetch lead data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setUnauthorized(false);
        
        const [leadRes, usersRes] = await Promise.all([
          leadsAPI.get(id),
          usersAPI.list()
        ]);
        
        setLead(leadRes.data);
        setEditedLead(leadRes.data);
        setUsers(usersRes.data || []);
        
        // Fetch activities
        try {
          const activitiesRes = await leadsAPI.activities(id);
          setActivities(activitiesRes.data || []);
        } catch {
          setActivities([]);
        }
        
        // Fetch demos for this lead
        try {
          const demosRes = await demosAPI.list();
          const allDemos = Array.isArray(demosRes.data) ? demosRes.data : [];
          setDemos(allDemos.filter(d => d.lead_id === id));
        } catch {
          setDemos([]);
        }
        
        // Fetch deals for this lead
        try {
          const dealsRes = await dealsAPI.list();
          const allDeals = Array.isArray(dealsRes.data) ? dealsRes.data : [];
          setDeals(allDeals.filter(d => d.lead_id === id));
        } catch {
          setDeals([]);
        }
        
      } catch (error) {
        console.error('Failed to fetch lead:', error);
        if (error.response?.status === 403) {
          setUnauthorized(true);
          setError('Unauthorized access. You can only view leads assigned to you.');
        } else if (error.response?.status === 404) {
          setError('Lead not found.');
        } else {
          setError('Failed to load lead details.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const refreshData = useCallback(async () => {
    try {
      const [leadRes, activitiesRes, demosRes] = await Promise.all([
        leadsAPI.get(id),
        leadsAPI.activities(id),
        demosAPI.list()
      ]);
      setLead(leadRes.data);
      setEditedLead(leadRes.data);
      setActivities(activitiesRes.data || []);
      const allDemos = Array.isArray(demosRes.data) ? demosRes.data : [];
      setDemos(allDemos.filter(d => d.lead_id === id));
    } catch (e) {
      console.error('Refresh failed:', e);
    }
  }, [id]);

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const getAssigneeName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  const getMinDate = () => {
    // Minimum date should always be today (no backdated callbacks)
    return new Date().toISOString().split('T')[0];
  };

  const handleSaveChanges = async () => {
    try {
      await leadsAPI.update(id, editedLead);
      setLead(editedLead);
      setIsEditing(false);
      await refreshData();
    } catch (error) {
      console.error('Failed to save lead:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to save changes',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleStatusChange = async () => {
    if (!statusChangeForm.notes.trim()) {
      toast({
        variant: 'destructive',
        title: 'Notes required',
        description: 'Notes are required for status change.'
      });
      return;
    }
    
    try {
      const data = {
        new_status: statusChangeForm.new_status,
        notes: statusChangeForm.notes
      };
      
      if (statusChangeForm.next_follow_up_date && statusChangeForm.next_follow_up_time) {
        data.next_follow_up_at = new Date(
          `${statusChangeForm.next_follow_up_date}T${statusChangeForm.next_follow_up_time}`
        ).toISOString();
      }
      
      await leadsAPI.changeStatus(id, data);
      setIsStatusChangeOpen(false);
      setStatusChangeForm({ new_status: '', notes: '', next_follow_up_date: '', next_follow_up_time: '' });
      await refreshData();
    } catch (error) {
      console.error('Failed to change status:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to change status',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleScheduleDemo = async () => {
    try {
      const demoDateTime = new Date(`${demoForm.demo_date}T${demoForm.demo_time}`);
      await demosAPI.create({
        lead_id: id,
        demo_datetime: demoDateTime.toISOString(),
        duration_minutes: demoForm.duration,
        meeting_link: demoForm.meeting_link,
        remarks: demoForm.notes
      });
      setIsScheduleDemoOpen(false);
      setDemoForm({ demo_date: '', demo_time: '', duration: '60', notes: '', meeting_link: '' });
      await refreshData();
    } catch (error) {
      console.error('Failed to schedule demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to schedule demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleRescheduleDemo = async () => {
    if (!selectedDemo) return;
    
    try {
      const demoDateTime = new Date(`${rescheduleForm.demo_date}T${rescheduleForm.demo_time}`);
      await demosAPI.reschedule(selectedDemo.id, {
        demo_datetime: demoDateTime.toISOString(),
        reschedule_reason: rescheduleForm.reschedule_reason,
        remarks: rescheduleForm.remarks
      });
      setIsRescheduleDemoOpen(false);
      setRescheduleForm({ demo_date: '', demo_time: '', reschedule_reason: '', remarks: '' });
      setSelectedDemo(null);
      await refreshData();
    } catch (error) {
      console.error('Failed to reschedule demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to reschedule demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleCompleteDemo = async () => {
    if (!selectedDemo) return;
    if (completeDemoForm.demo_done_by.filter(n => n.trim()).length === 0) {
      toast({
        variant: 'destructive',
        title: 'Missing details',
        description: 'Please enter at least one person who conducted the demo.'
      });
      return;
    }
    
    try {
      await demosAPI.complete(selectedDemo.id, {
        demo_done_by: completeDemoForm.demo_done_by.filter(n => n.trim()),
        demo_rating: completeDemoForm.demo_rating,
        demo_actual_start: new Date(completeDemoForm.demo_actual_start).toISOString(),
        demo_actual_end: new Date(completeDemoForm.demo_actual_end).toISOString(),
        demo_notes: completeDemoForm.demo_notes
      });
      setIsCompleteDemoOpen(false);
      setCompleteDemoForm({ demo_done_by: [''], demo_rating: 5, demo_actual_start: '', demo_actual_end: '', demo_notes: '' });
      setSelectedDemo(null);
      await refreshData();
    } catch (error) {
      console.error('Failed to complete demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to complete demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteLead = async () => {
    try {
      // First check for related data
      const relationsRes = await leadsAPI.checkRelations(id);
      const relations = relationsRes.data;
      
      if (relations.has_relations) {
        // Show detailed warning
        const confirmed = await confirm({
          title: 'Delete this lead?',
          description: `⚠️ WARNING: This lead has related data that will be permanently deleted:\n\n• ${relations.relations.demos} Demo(s)\n• ${relations.relations.deals} Deal(s)\n• ${relations.relations.clients} Client(s) (converted deals)\n• ${relations.relations.incentive_records} Incentive Record(s)\n• ${relations.relations.company_receivables} Company Receivable(s)\n\nAre you sure you want to delete "${relations.company_name}" and ALL related data?\n\nThis action CANNOT be undone!`,
          confirmText: 'Delete',
          cancelText: 'Cancel',
          destructive: true
        });
        
        if (!confirmed) return;
        
        // Delete with confirmation
        await leadsAPI.delete(id, true);
      } else {
        // No relations, just delete
        const confirmed = await confirm({
          title: 'Delete this lead?',
          description: 'This action cannot be undone.',
          confirmText: 'Delete',
          cancelText: 'Cancel',
          destructive: true
        });
        if (!confirmed) return;
        
        await leadsAPI.delete(id, false);
      }
      
      navigate('/leads');
    } catch (error) {
      console.error('Failed to delete lead:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete lead',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleReassign = async () => {
    try {
      await leadsAPI.reassign(id, reassignForm.new_assignee_id, reassignForm.notes);
      setIsReassignOpen(false);
      setReassignForm({ new_assignee_id: '', notes: '' });
      await refreshData();
    } catch (error) {
      console.error('Failed to reassign lead:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to reassign lead',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleQualifyDemo = async (demoId) => {
    try {
      await demosAPI.markSQD(demoId, 'Qualified by manager/admin');
      await refreshData();
    } catch (error) {
      console.error('Failed to qualify demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to mark SQD',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const addDemoPerson = () => {
    setCompleteDemoForm(prev => ({
      ...prev,
      demo_done_by: [...prev.demo_done_by, '']
    }));
  };

  const updateDemoPerson = (index, value) => {
    setCompleteDemoForm(prev => {
      const updated = [...prev.demo_done_by];
      updated[index] = value;
      return { ...prev, demo_done_by: updated };
    });
  };

  const removeDemoPerson = (index) => {
    if (completeDemoForm.demo_done_by.length > 1) {
      setCompleteDemoForm(prev => ({
        ...prev,
        demo_done_by: prev.demo_done_by.filter((_, i) => i !== index)
      }));
    }
  };

  const isAdminOrManager = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading lead...</span>
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Shield className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-slate-800 mb-2">Unauthorized Access</h2>
        <p className="text-slate-600 mb-4">{error}</p>
        <p className="text-slate-500">Redirecting to leads page in <span className="font-bold text-red-500">{countdown}</span> seconds...</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/leads')}>
          Go to Leads Now
        </Button>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-slate-400 mb-4" />
        <p className="text-lg text-slate-600">{error || 'Lead not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/leads')}>
          Back to Leads
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leads')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{lead.company_name}</h1>
            <p className="text-slate-500">{lead.contact_name} • {lead.contact_phone}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={cn('text-sm px-3 py-1', getStatusColor(lead.current_status))}>
            {formatStatus(lead.current_status)}
          </Badge>
          
          {!hasCompletedDemo && (
            <Button variant="outline" onClick={() => setIsEditing(!isEditing)}>
              <Edit className="w-4 h-4 mr-2" />
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
          )}
          
          {hasCompletedDemo && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Demo Completed - Lead Locked
            </Badge>
          )}
          
          {hasScheduledDemo ? (
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                const activeDemo = demos.find(d => d.status === 'SCHEDULED' || d.status === 'RESCHEDULED');
                if (activeDemo) {
                  setSelectedDemo(activeDemo);
                  setIsRescheduleDemoOpen(true);
                }
              }}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Reschedule Demo
            </Button>
          ) : (
            <Button 
              className="bg-slate-800 hover:bg-slate-900"
              onClick={handleScheduleDemoClick}
              disabled={hasCompletedDemo}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Demo
            </Button>
          )}
          
          {isAdminOrManager && (
            <>
              <Button variant="outline" onClick={() => setIsReassignOpen(true)}>
                <UserCheck className="w-4 h-4 mr-2" />
                Reassign
              </Button>
              <Button variant="destructive" onClick={() => setIsDeleteLeadOpen(true)}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {hasCompletedDemo && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            This lead cannot be edited because a demo has been completed.
          </p>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="-mx-4 px-4 overflow-x-auto scrollbar-hide">
          <TabsList className="min-w-max">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="chat" data-testid="chat-history-tab">Chat History</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="demos">Demos ({demos.length})</TabsTrigger>
            <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="details" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Lead Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Card */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Phone className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Phone</p>
                        <p className="font-medium text-slate-800">{lead.contact_phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Mail className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium text-slate-800">{lead.contact_email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Building2 className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Industry</p>
                        <p className="font-medium text-slate-800">{lead.industry || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">City</p>
                        <p className="font-medium text-slate-800">{lead.city || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-200">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      asChild
                      data-testid="call-cta-btn"
                    >
                      <a href={`tel:${lead.contact_phone || lead.poc_mobile || ''}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      asChild
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      data-testid="whatsapp-cta-btn"
                    >
                      <a 
                        href={`https://wa.me/${(lead.contact_phone || lead.poc_mobile || '').replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        WhatsApp
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      asChild
                      data-testid="email-cta-btn"
                    >
                      <a href={`mailto:${lead.contact_email || ''}`}>
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Enrichment Details (Editable) */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Lead Details & Enrichment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Real Company Name</Label>
                      {isEditing ? (
                        <Input
                          value={editedLead.real_company_name || ''}
                          onChange={(e) => setEditedLead({...editedLead, real_company_name: e.target.value})}
                        />
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.real_company_name || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Director Name</Label>
                      {isEditing ? (
                        <Input
                          value={editedLead.director_name || ''}
                          onChange={(e) => setEditedLead({...editedLead, director_name: e.target.value})}
                        />
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.director_name || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>POC Name</Label>
                      {isEditing ? (
                        <Input
                          value={editedLead.poc_name || ''}
                          onChange={(e) => setEditedLead({...editedLead, poc_name: e.target.value})}
                        />
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.poc_name || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>POC Mobile</Label>
                      {isEditing ? (
                        <Input
                          value={editedLead.poc_mobile || ''}
                          onChange={(e) => setEditedLead({...editedLead, poc_mobile: e.target.value})}
                        />
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.poc_mobile || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Total Employee Count</Label>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editedLead.total_employee_count || ''}
                          onChange={(e) => setEditedLead({...editedLead, total_employee_count: parseInt(e.target.value)})}
                        />
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.total_employee_count || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      {isEditing ? (
                        <Input
                          value={editedLead.city || ''}
                          onChange={(e) => setEditedLead({...editedLead, city: e.target.value})}
                        />
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.city || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Industry</Label>
                      {isEditing ? (
                        <Select
                          value={editedLead.industry || ''}
                          onValueChange={(v) => setEditedLead({...editedLead, industry: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map(ind => (
                              <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.industry || '-'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Using HRMS Currently?</Label>
                      {isEditing ? (
                        <Select
                          value={editedLead.using_hrms_currently ? 'yes' : 'no'}
                          onValueChange={(v) => setEditedLead({...editedLead, using_hrms_currently: v === 'yes'})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-slate-800 p-2 bg-slate-50 rounded">
                          {lead.using_hrms_currently ? 'Yes' : 'No'}
                        </p>
                      )}
                    </div>
                    {(isEditing ? editedLead.using_hrms_currently : lead.using_hrms_currently) && (
                      <div className="space-y-2">
                        <Label>Current HRMS Tool</Label>
                        {isEditing ? (
                          <Select
                            value={editedLead.current_hrms_tool || ''}
                            onValueChange={(v) => setEditedLead({...editedLead, current_hrms_tool: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {hrmsTools.map(tool => (
                                <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-slate-800 p-2 bg-slate-50 rounded">{lead.current_hrms_tool || '-'}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditing && (
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-200">
                      <Button variant="outline" onClick={() => { setIsEditing(false); setEditedLead(lead); }}>Cancel</Button>
                      <Button className="bg-slate-800 hover:bg-slate-900" onClick={handleSaveChanges}>
                        Save Changes
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes Section */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <Textarea
                      value={editedLead.notes || ''}
                      onChange={(e) => setEditedLead({...editedLead, notes: e.target.value})}
                      placeholder="Add notes about this lead..."
                      rows={4}
                    />
                  ) : (
                    <div className="p-3 bg-slate-50 rounded-lg min-h-[100px]">
                      <p className="text-slate-700 whitespace-pre-wrap">{lead.notes || 'No notes added yet.'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Summary */}
            <div className="space-y-6">
              {/* Lead Score */}
              <Card className="bg-white border-slate-200">
                <CardContent className="p-5">
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-2">Lead Score</p>
                    <div className="relative w-24 h-24 mx-auto">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="48" cy="48" r="40" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                        <circle
                          cx="48" cy="48" r="40" fill="none"
                          stroke={lead.priority_score >= 80 ? '#10b981' : lead.priority_score >= 60 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="8"
                          strokeDasharray={`${lead.priority_score * 2.51} 251`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-slate-800">{lead.priority_score}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assigned To */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm">Assigned To</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium">
                      {getAssigneeName(lead.assigned_to_user_id).split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{getAssigneeName(lead.assigned_to_user_id)}</p>
                      <p className="text-xs text-slate-500">Sales Representative</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Source Channel */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm">Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge variant="outline" className="text-sm">
                    {lead.source_channel?.replace(/_/g, ' ') || 'Not specified'}
                  </Badge>
                </CardContent>
              </Card>

              {/* Created Info */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm">Created</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">
                    {new Date(lead.created_at).toLocaleString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {lead.tags?.length > 0 ? lead.tags.map((tag, idx) => (
                      <Badge key={typeof tag === 'object' ? tag.id : idx} variant="outline">
                        {typeof tag === 'object' ? tag.name : tag}
                      </Badge>
                    )) : (
                      <p className="text-sm text-slate-500">No tags</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-6" data-testid="chat-history-tab-content">
          <WhatsAppChatHistory 
            leadId={id} 
            contactPhone={lead.contact_phone || lead.poc_mobile}
          />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Complete Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activities.length > 0 ? activities.map((activity, index) => (
                  <div key={activity.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', getActivityColor(activity.activity_type))}>
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      {index < activities.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 mt-2" />}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="font-medium text-slate-800">
                          {activity.activity_type.replace(/_/g, ' ')}
                        </p>
                        <span className="text-xs text-slate-500">
                          {new Date(activity.activity_timestamp).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {activity.previous_status && activity.new_status && (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{formatStatus(activity.previous_status)}</Badge>
                          <span className="text-slate-400">→</span>
                          <Badge className={cn('text-xs', getStatusColor(activity.new_status))}>{formatStatus(activity.new_status)}</Badge>
                        </div>
                      )}
                      {activity.comment && (
                        <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">{activity.comment}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">by {activity.user_name || 'System'}</p>
                    </div>
                  </div>
                )) : (
                  <p className="text-slate-500 text-center py-8">No activities recorded yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demos" className="mt-6">
          <Card className="bg-white border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Demos</CardTitle>
            </CardHeader>
            <CardContent>
              {demos.length > 0 ? (
                <div className="space-y-4">
                  {demos.map(demo => (
                    <div key={demo.id} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className={cn(
                            'text-xs',
                            demo.status === 'SCHEDULED' && 'bg-blue-100 text-blue-700',
                            demo.status === 'RESCHEDULED' && 'bg-orange-100 text-orange-700',
                            demo.status === 'COMPLETED_QUALIFIED_SQD' && 'bg-emerald-100 text-emerald-700',
                            demo.status === 'COMPLETED_NOT_QUALIFIED' && 'bg-amber-100 text-amber-700'
                          )}>
                            {demo.status.replace(/_/g, ' ')}
                          </Badge>
                          {demo.is_sqd && <Badge className="bg-emerald-500 text-white text-xs">SQD</Badge>}
                        </div>
                        <div className="flex gap-2">
                          {(demo.status === 'SCHEDULED' || demo.status === 'RESCHEDULED') && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedDemo(demo);
                                  // Prefill date/time from demo's scheduled datetime
                                  const demoTime = new Date(demo.demo_datetime);
                                  const durationMins = parseInt(demo.duration_minutes) || 60;
                                  const endTime = new Date(demoTime.getTime() + durationMins * 60000);
                                  
                                  // Format for datetime-local input
                                  const formatDateTimeLocal = (date) => {
                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const hours = String(date.getHours()).padStart(2, '0');
                                    const mins = String(date.getMinutes()).padStart(2, '0');
                                    return `${year}-${month}-${day}T${hours}:${mins}`;
                                  };
                                  
                                  setCompleteDemoForm({
                                    ...completeDemoForm,
                                    demo_actual_start: formatDateTimeLocal(demoTime),
                                    demo_actual_end: formatDateTimeLocal(endTime)
                                  });
                                  setIsCompleteDemoOpen(true);
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Mark Done
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedDemo(demo);
                                  setIsRescheduleDemoOpen(true);
                                }}
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Reschedule
                              </Button>
                            </>
                          )}
                          {demo.status === 'COMPLETED_NOT_QUALIFIED' && isAdminOrManager && (
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleQualifyDemo(demo.id)}
                            >
                              <Shield className="w-4 h-4 mr-1" />
                              Qualify (SQD)
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">Scheduled For</p>
                          <p className="font-medium">
                            {new Date(demo.demo_datetime).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Duration</p>
                          <p className="font-medium">{demo.duration_minutes || 60} mins</p>
                        </div>
                        {demo.reschedule_count && parseInt(demo.reschedule_count) > 0 && (
                          <div className="col-span-2">
                            <p className="text-slate-500">Rescheduled</p>
                            <p className="font-medium text-orange-600">
                              {demo.reschedule_count}x - From {new Date(demo.rescheduled_from).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        )}
                        {demo.demo_rating && (
                          <div>
                            <p className="text-slate-500">Rating</p>
                            <p className="font-medium flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              {demo.demo_rating}/10
                            </p>
                          </div>
                        )}
                        {demo.demo_done_by && (
                          <div>
                            <p className="text-slate-500">Done By</p>
                            <p className="font-medium">{typeof demo.demo_done_by === 'string' ? JSON.parse(demo.demo_done_by).join(', ') : demo.demo_done_by}</p>
                          </div>
                        )}
                      </div>
                      
                      {demo.demo_notes && (
                        <div className="mt-3 p-2 bg-slate-50 rounded text-sm">
                          <p className="text-slate-500 mb-1">Demo Notes:</p>
                          <p className="text-slate-700">{demo.demo_notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No demos scheduled for this lead</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals" className="mt-6">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Deals</CardTitle>
            </CardHeader>
            <CardContent>
              {deals.length > 0 ? (
                <div className="space-y-4">
                  {deals.map(deal => (
                    <div key={deal.id} className="p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/deals`)}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-800">{deal.company_name}</h4>
                        <Badge>{deal.deal_stage?.replace(/_/g, ' ')}</Badge>
                      </div>
                      <p className="text-lg font-bold text-emerald-600">₹{deal.total_amount?.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-slate-500">{deal.plan_name} • {deal.total_users} users</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">No deals created for this lead</p>
                  {lead.current_status === 'DEMO_QUALIFIED_SQD' && (
                    <Button onClick={() => navigate('/deals')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Deal
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Change Modal */}
      <Dialog open={isStatusChangeOpen} onOpenChange={setIsStatusChangeOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Change Lead Status</DialogTitle>
            <DialogDescription>
              Change the status of this lead. Notes are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>New Status *</Label>
              <Select value={statusChangeForm.new_status} onValueChange={(v) => setStatusChangeForm({...statusChangeForm, new_status: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {leadStatuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes * (Required)</Label>
              <Textarea
                value={statusChangeForm.notes}
                onChange={(e) => setStatusChangeForm({...statusChangeForm, notes: e.target.value})}
                placeholder="Why is this status being changed?"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Next Follow-up Date</Label>
                <Input
                  type="date"
                  value={statusChangeForm.next_follow_up_date}
                  min={getMinDate()}
                  onChange={(e) => setStatusChangeForm({...statusChangeForm, next_follow_up_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Follow-up Time</Label>
                <Input
                  type="time"
                  value={statusChangeForm.next_follow_up_time}
                  onChange={(e) => setStatusChangeForm({...statusChangeForm, next_follow_up_time: e.target.value})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusChangeOpen(false)}>Cancel</Button>
            <Button
              onClick={handleStatusChange}
              disabled={!statusChangeForm.new_status || !statusChangeForm.notes.trim()}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Demo Modal */}
      <Dialog open={isScheduleDemoOpen} onOpenChange={setIsScheduleDemoOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Schedule Demo</DialogTitle>
            <DialogDescription>
              Schedule a demo for {lead.company_name}. Date must be after lead creation ({new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {demoForm.demo_date && new Date(demoForm.demo_date) < new Date(lead.created_at) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                ⚠️ Demo date cannot be before lead creation date
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Demo Date *</Label>
                <Input
                  type="date"
                  value={demoForm.demo_date}
                  min={getMinDate()}
                  onChange={(e) => setDemoForm({...demoForm, demo_date: e.target.value})}
                />
                <p className="text-xs text-slate-500">Min: {getMinDate()}</p>
              </div>
              <div className="space-y-2">
                <Label>Start Time * (use 24hr format)</Label>
                <Input
                  type="time"
                  value={demoForm.demo_time}
                  onChange={(e) => setDemoForm({...demoForm, demo_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Duration (minutes)</Label>
              <Select value={demoForm.duration} onValueChange={(v) => setDemoForm({...demoForm, duration: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                  <SelectItem value="90">90 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Meeting Link (optional)</Label>
              <Input
                value={demoForm.meeting_link}
                onChange={(e) => setDemoForm({...demoForm, meeting_link: e.target.value})}
                placeholder="https://meet.google.com/..."
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={demoForm.notes}
                onChange={(e) => setDemoForm({...demoForm, notes: e.target.value})}
                placeholder="Additional notes for the demo..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsScheduleDemoOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleScheduleDemo}
              disabled={!demoForm.demo_date || !demoForm.demo_time}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Schedule Demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Demo Modal */}
      <Dialog open={isRescheduleDemoOpen} onOpenChange={setIsRescheduleDemoOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reschedule Demo</DialogTitle>
            <DialogDescription>
              Reschedule the demo. Original: {selectedDemo && new Date(selectedDemo.demo_datetime).toLocaleString('en-IN')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>New Date *</Label>
                <Input
                  type="date"
                  value={rescheduleForm.demo_date}
                  min={getMinDate()}
                  onChange={(e) => setRescheduleForm({...rescheduleForm, demo_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>New Time *</Label>
                <Input
                  type="time"
                  value={rescheduleForm.demo_time}
                  onChange={(e) => setRescheduleForm({...rescheduleForm, demo_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason for Rescheduling</Label>
              <Textarea
                value={rescheduleForm.reschedule_reason}
                onChange={(e) => setRescheduleForm({...rescheduleForm, reschedule_reason: e.target.value})}
                placeholder="Why is the demo being rescheduled?"
              />
            </div>
            <div className="space-y-2">
              <Label>Additional Remarks</Label>
              <Textarea
                value={rescheduleForm.remarks}
                onChange={(e) => setRescheduleForm({...rescheduleForm, remarks: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsRescheduleDemoOpen(false); setSelectedDemo(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleRescheduleDemo}
              disabled={!rescheduleForm.demo_date || !rescheduleForm.demo_time}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Reschedule Demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Demo Modal */}
      <Dialog open={isCompleteDemoOpen} onOpenChange={setIsCompleteDemoOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Mark Demo as Complete</DialogTitle>
            <DialogDescription>
              Enter the demo completion details. This will lock the lead from editing.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Demo Conducted By * (SaaS Company Persons)</Label>
              {completeDemoForm.demo_done_by.map((person, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={person}
                    onChange={(e) => updateDemoPerson(index, e.target.value)}
                    placeholder={`Person ${index + 1} name`}
                  />
                  {completeDemoForm.demo_done_by.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeDemoPerson(index)}>
                      <XCircle className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addDemoPerson}>
                <Plus className="w-4 h-4 mr-1" /> Add Person
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Demo Rating * (out of 10)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={completeDemoForm.demo_rating}
                  onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_rating: Math.min(10, Math.max(1, parseInt(e.target.value) || 1))})}
                  className="w-24"
                />
                <div className="flex">
                  {[...Array(10)].map((_, i) => (
                    <Star
                      key={i}
                      className={cn(
                        'w-5 h-5 cursor-pointer',
                        i < completeDemoForm.demo_rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'
                      )}
                      onClick={() => setCompleteDemoForm({...completeDemoForm, demo_rating: i + 1})}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Demo Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={completeDemoForm.demo_actual_start}
                  onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_actual_start: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Demo End Time *</Label>
                <Input
                  type="datetime-local"
                  value={completeDemoForm.demo_actual_end}
                  onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_actual_end: e.target.value})}
                />
              </div>
            </div>
            
            {completeDemoForm.demo_actual_start && completeDemoForm.demo_actual_end && (
              <div className="p-3 bg-slate-100 rounded-lg flex items-center gap-2">
                <Timer className="w-5 h-5 text-slate-600" />
                <span className="font-medium">Total Duration: {calculateDemoDuration()} minutes</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Demo Notes</Label>
              <Textarea
                value={completeDemoForm.demo_notes}
                onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_notes: e.target.value})}
                placeholder="Key takeaways, client feedback, next steps..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCompleteDemoOpen(false); setSelectedDemo(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCompleteDemo}
              disabled={
                completeDemoForm.demo_done_by.filter(n => n.trim()).length === 0 ||
                !completeDemoForm.demo_actual_start ||
                !completeDemoForm.demo_actual_end
              }
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Lead Modal */}
      <Dialog open={isReassignOpen} onOpenChange={setIsReassignOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Reassign Lead</DialogTitle>
            <DialogDescription>
              Reassign this lead to another team member.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Assign To *</Label>
              <Select value={reassignForm.new_assignee_id} onValueChange={(v) => setReassignForm({...reassignForm, new_assignee_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.id !== lead.assigned_to_user_id).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reassignment Notes</Label>
              <Textarea
                value={reassignForm.notes}
                onChange={(e) => setReassignForm({...reassignForm, notes: e.target.value})}
                placeholder="Why is this lead being reassigned?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReassignOpen(false)}>Cancel</Button>
            <Button
              onClick={handleReassign}
              disabled={!reassignForm.new_assignee_id}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Reassign Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Lead Confirmation */}
      <AlertDialog open={isDeleteLeadOpen} onOpenChange={setIsDeleteLeadOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2">
                <p>Are you sure you want to delete this lead? This action cannot be undone.</p>
                {demos.length > 0 && (
                  <p className="text-amber-600 font-medium">
                    ⚠️ This will also delete {demos.length} demo(s) associated with this lead.
                  </p>
                )}
                {deals.length > 0 && (
                  <p className="text-red-600 font-medium">
                    ⚠️ This will also delete {deals.length} deal(s) associated with this lead.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLead} className="bg-red-600 hover:bg-red-700">
              Delete Lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Composer */}
      <EmailComposer
        isOpen={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
        recipientEmail={lead.contact_email}
        recipientName={lead.contact_name}
        companyName={lead.company_name}
      />

      {/* Missing Fields Input Dialog - Fill missing fields inline before scheduling demo */}
      <Dialog open={missingFieldsOpen} onOpenChange={setMissingFieldsOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Complete Required Information
            </DialogTitle>
            <DialogDescription>
              Please fill in the following required fields to schedule a demo:
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {missingFields.includes('Email') && (
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editedLead?.contact_email || ''}
                  onChange={(e) => setEditedLead({...editedLead, contact_email: e.target.value})}
                  placeholder="email@company.com"
                />
              </div>
            )}
            {missingFields.includes('City') && (
              <div className="space-y-2">
                <Label>City *</Label>
                <Input
                  value={editedLead?.city || ''}
                  onChange={(e) => setEditedLead({...editedLead, city: e.target.value})}
                  placeholder="Mumbai"
                />
              </div>
            )}
            {missingFields.includes('Employee Count') && (
              <div className="space-y-2">
                <Label>Employee Count *</Label>
                <Input
                  type="number"
                  value={editedLead?.total_employee_count || ''}
                  onChange={(e) => setEditedLead({...editedLead, total_employee_count: parseInt(e.target.value) || 0})}
                  placeholder="100"
                />
              </div>
            )}
            {missingFields.includes('Industry') && (
              <div className="space-y-2">
                <Label>Industry *</Label>
                <Select
                  value={editedLead?.industry || ''}
                  onValueChange={(v) => setEditedLead({...editedLead, industry: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {missingFields.includes('Using HRMS Currently') && (
              <div className="space-y-2">
                <Label>Using HRMS Currently? *</Label>
                <Select
                  value={editedLead?.using_hrms_currently === true ? 'yes' : editedLead?.using_hrms_currently === false ? 'no' : ''}
                  onValueChange={(v) => setEditedLead({...editedLead, using_hrms_currently: v === 'yes'})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(missingFields.includes('Current HRMS Tool') || editedLead?.using_hrms_currently === true) && !editedLead?.current_hrms_tool && (
              <div className="space-y-2">
                <Label>Current HRMS Tool *</Label>
                <Select
                  value={editedLead?.current_hrms_tool || ''}
                  onValueChange={(v) => setEditedLead({...editedLead, current_hrms_tool: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tool" />
                  </SelectTrigger>
                  <SelectContent>
                    {hrmsTools.map(tool => (
                      <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingFieldsOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  // Save the updated lead first
                  await leadsAPI.update(id, editedLead);
                  setLead(editedLead);
                  setMissingFieldsOpen(false);
                  // Now open the schedule demo modal
                  setIsScheduleDemoOpen(true);
                } catch (error) {
                  toast({
                    variant: 'destructive',
                    title: 'Failed to save lead',
                    description: error.response?.data?.detail || error.message
                  });
                }
              }}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Save & Schedule Demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog {...dialogProps} />
    </div>
  );
};

export default LeadDetailPage;
